import { api } from './api';

/** One invited friend's journey + the held-reward status (the "coach" view). */
export interface ReferralFriend {
  id: number;
  name: string;
  status: string;
  signed_up: boolean;
  activated: boolean;
  converted: boolean;
  /** pending_holdback | granted | void | null (not yet converted). */
  conversion_reward_status: string | null;
  /** When a held conversion reward is scheduled to land (ISO), else null. */
  conversion_reward_releases_at: string | null;
}

export interface ReferralOverview {
  enabled: boolean;
  code: string | null;
  link: string | null;
  conversion_holdback_days: number;
  referrals: ReferralFriend[];
}

/** The signed-in user's referral code/link + everyone they've invited. */
export async function getReferralOverview(): Promise<ReferralOverview> {
  const response = await api.get<ReferralOverview>('/referral');
  return response.data;
}

/**
 * Resolve a referral code to the inviter's first name for the "{Friend} invited
 * you" register banner. Public; returns null when the code is unknown or referral
 * is off. Never throws — a bad code must not break the register page.
 */
export async function resolveReferralCode(code: string): Promise<string | null> {
  try {
    const response = await api.get<{ referrer_name: string | null }>(
      `/referral/resolve/${encodeURIComponent(code)}`,
    );
    return response.data.referrer_name;
  } catch {
    return null;
  }
}

/**
 * The invite code stashed by the register page (?ref=CODE), format-validated —
 * matching the server's rule — so an out-of-format value (older build, crafted
 * link) is never sent. Shared by every signup path: email, Google (rides the
 * OAuth state), and Apple (rides the token-exchange POST).
 */
export function getStashedReferralCode(): string | null {
  try {
    const stored = localStorage.getItem('referral_code');
    return stored && /^[A-Za-z0-9]{1,16}$/.test(stored) ? stored : null;
  } catch {
    return null; // Safari private mode
  }
}

/**
 * Capture whether a referral code is stashed, then CLEAR the stash, in one
 * move. The atom every OAuth conversion site must use: capture-then-clear as
 * two separate steps kept getting half-implemented — clear-before-capture
 * made every referred OAuth signup report false (Codex P2 on #818), and
 * capture-without-clear let the code mis-attach to a later signup on the
 * same device (Codex P2 on #819, the waitlisted-creation branches). One
 * function, wrong order no longer expressible.
 *
 * The email register path deliberately does NOT use this: it needs the code
 * VALUE for the API body, not just the flag.
 */
export function consumeStashedReferralFlag(): boolean {
  const referred = !!getStashedReferralCode();
  try {
    localStorage.removeItem('referral_code');
  } catch {
    /* ignore — Safari private mode */
  }
  return referred;
}
